import * as jotai from 'jotai'

import * as api from '@owl-nest/api-client/latest'
import * as hooks from '@owl-nest/hooks'
import { listForProject } from './api'

export type ProjectOrderParams<EXTRA_FIELDS extends string> = {
  extraFields?: EXTRA_FIELDS[]
  is_donation?: string
  page?: number
  subscriptionsPage?: number
  limit?: number
  payment_method?: api.PaymentMethod | 'all' | 'card,maestro,creditcard'
  q?: string
  reverse?: boolean
  referral_user_id?: number
  reward_id?: string | number | 'all'
  sort?: string
  status?: api.OrderStatus | 'all'
  show_anonymous?: boolean
  tip?: string
  type?: 'monthly' | 'single' | 'all'
  view_type?: string
  with_facets?: boolean
}
type PageableOrderList<EXTRA_FIELDS extends string = ''> = api.WithOrderFacets<
  api.Pageable<api.OrderList<any, EXTRA_FIELDS>>
>
type ProjectOrderConfig<EXTRA_FIELDS extends string = ''> = {
  fetchOnMount?: boolean | 'force'
  withToken?: boolean
  params?: ProjectOrderParams<EXTRA_FIELDS>
}

export async function create(
  projectId: number | string,
  payload: api.CreateOrder,
  withToken = payload.dry_run ? false : true,
): Promise<api.ApiResponse<api.Order>> {
  const response = await api.api.post.projectOrders({
    body: payload,
    urlParams: { projectId: String(projectId) },
    withToken,
  })

  return response.next((success) => success.body)
}

export function projectOrderParamsToURLSearchParams<EXTRA_FIELDS extends string>(
  params: ProjectOrderParams<EXTRA_FIELDS>,
): URLSearchParams {
  const searchParams = new URLSearchParams()

  if (params === undefined) {
    return searchParams
  }

  if (params.extraFields !== undefined) {
    searchParams.set('extra_fields', String(params.extraFields))
  }
  if (params.is_donation !== undefined) {
    searchParams.set('is_donation', String(params.is_donation))
  }
  if (params.limit !== undefined) {
    searchParams.set('limit', String(params.limit))
  }
  if (params.show_anonymous !== undefined) {
    searchParams.set('show_anonymous', 'true')
  }
  if (params.page !== undefined) {
    searchParams.set('offset', String((params.page - 1) * (params.limit ?? 20)))
  }
  if (params.payment_method !== undefined) {
    searchParams.set('payment_method', String(params.payment_method))
  }
  if (params.q !== undefined) {
    searchParams.set('q', String(params.q))
  }
  if (params.reverse !== undefined) {
    searchParams.set('reverse', String(params.reverse))
  }
  if (params.reward_id && params.reward_id !== 'all') {
    searchParams.set('reward_id', String(params.reward_id))
  }
  if (params.sort !== undefined) {
    searchParams.set('sort', String(params.sort))
  }
  if (params.status !== undefined) {
    searchParams.set('status', String(params.status))
  }
  if (params.tip !== undefined) {
    searchParams.set('tip', String(params.tip))
  }
  if (params.type !== undefined) {
    searchParams.set('type', String(params.type))
  }
  if (params.with_facets !== undefined) {
    searchParams.set('with_facets', 'true')
  }
  if (params.view_type !== undefined) {
    searchParams.set('view_type', String(params.view_type))
  }
  if (params.referral_user_id !== undefined) {
    searchParams.set('referral_user_id', String(params.referral_user_id))
  }

  return searchParams
}

const PROJECT_ORDERS_CACHE = jotai.atom<hooks.Cache<PageableOrderList[]>>({})

export function useProjectOrders<EXTRA_FIELDS extends string>(
  projectId?: number,
  { fetchOnMount = true, params = {}, withToken }: ProjectOrderConfig<EXTRA_FIELDS> = {},
) {
  const sharedQuery = hooks.useSharedQuery(
    async (projectId: number | string, params: ProjectOrderParams<EXTRA_FIELDS>, withToken: boolean | undefined) => {
      const response = await listForProject<EXTRA_FIELDS>(projectId, {
        getParams: projectOrderParamsToURLSearchParams<EXTRA_FIELDS>(params),
        withToken,
      })
      return response.next((success) => [success])
    },
    {
      atom: PROJECT_ORDERS_CACHE,
      id,
    },
  )

  hooks.useFetchOnMount({ fetchOnMount, query, getResponse }, id(projectId, params, withToken))

  const response = getResponse()

  return {
    facets: getResponse().data?.[0].facets,
    meta: getLastMeta(),
    query,
    queryNext,
    response: getResponse(),
  }

  function getResponse(): hooks.Response<PageableOrderList<EXTRA_FIELDS>[]> {
    if (projectId === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }

    return sharedQuery.getCurrentResponse([projectId, params, withToken]) as hooks.Response<
      PageableOrderList<EXTRA_FIELDS>[]
    >
  }

  function getLastMeta():
    | {
        limit: number
        next: string | null
        total_count: number
        offset?: number | undefined
      }
    | undefined {
    if (response.status === hooks.QueryStatus.PRISTINE) {
      console.warn(`[services/order] getLastMeta() can't run if status is pristine`)
      return
    }

    const data = response?.data

    return data?.length ? data[data.length - 1].meta : undefined
  }

  function query(): Promise<void> | undefined {
    if (projectId) {
      return sharedQuery.query(projectId, params, withToken)
    }
  }

  async function queryNext(): Promise<void> {
    if (projectId === undefined) {
      return
    }

    const response = getResponse()
    const next =
      response?.data !== undefined ? (response.data[response.data.length - 1].meta?.next ?? undefined) : undefined
    const nextSearchParam = new URLSearchParams(next)

    if (next === undefined) {
      console.warn(`[services/order] queryNext(), next don't exist`)
      return
    }

    sharedQuery.patch([projectId, params, withToken], async (response) => {
      const ordersResponse = await listForProject<EXTRA_FIELDS>(projectId, {
        getParams: nextSearchParam,
        withToken: withToken,
      })

      return ordersResponse.next((success) => {
        let orders = []
        if (response.data !== undefined) {
          orders = [...response.data, success]
        } else {
          orders = [success]
        }

        return orders
      })
    })
  }
}

function id<EXTRA_FIELDS extends string>(
  projectId: number | string | undefined,
  config: ProjectOrderParams<EXTRA_FIELDS>,
  withToken: boolean | undefined,
): string {
  return JSON.stringify([
    projectId,
    config.extraFields?.sort() ?? [],
    config.limit,
    config.page,
    config.subscriptionsPage,
    config.payment_method,
    config.q,
    config.referral_user_id,
    config.reverse,
    config.reward_id,
    config.sort,
    config.status,
    config.tip,
    config.is_donation,
    config.type,
    config.with_facets,
    withToken,
  ])
}
